import { Link, useLocation } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { useDueCounts } from "../modal/useDueCounts";
import { conversationsPath, dueTodayPath, type DueCounts } from "../api/OutreachTouchesApi";

/** True on any page under the CRM that should carry the strip (everything except the LinkedIn Outreach pages themselves). */
export function showsDueStrip(pathname: string): boolean {
  return /^\/(admin|employee)\/crm(\/|$)/.test(pathname) && !/\/crm\/due-today(\/pipeline|\/performance)?\/?$/.test(pathname);
}

export function dueSummaryParts(c: DueCounts): string[] {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const parts: string[] = [];
  if (c.requests_to_send) parts.push(plural(c.requests_to_send, "request to send", "requests to send"));
  if (c.requests_to_check) parts.push(plural(c.requests_to_check, "request to check", "requests to check"));
  if (c.messages) parts.push(plural(c.messages, "message", "messages"));
  if (c.follow_ups) parts.push(plural(c.follow_ups, "follow-up", "follow-ups"));
  if (c.needs) parts.push(`${c.needs} need${c.needs === 1 ? "s" : ""} a profile or person`);
  if (c.conversations_waiting_on_us) parts.push(plural(c.conversations_waiting_on_us, "reply waiting on us", "replies waiting on us"));
  if (c.conversations_no_answer) parts.push(`${c.conversations_no_answer} with no answer`);
  if (c.warm_leads) parts.push(plural(c.warm_leads, "warm company to write to", "warm companies to write to"));
  return parts;
}

/**
 * Slim, undismissable strip above the content of every CRM page: one line with what is due and a button to the due-today
 * page. Shown only when something is due or overdue, and only to users with CRM access (the caller passes `allowed`).
 */
export default function DueTodayStrip({ allowed }: { allowed: boolean }) {
  const { pathname } = useLocation();
  const visible = allowed && showsDueStrip(pathname);
  const { counts } = useDueCounts(visible, pathname);
  const replies = (counts?.conversations_waiting_on_us ?? 0) + (counts?.conversations_no_answer ?? 0);
  if (!visible || !counts || counts.due_total + replies + (counts.warm_leads ?? 0) <= 0) return null;
  const overdue = counts.overdue > 0;
  return (
    <div
      role="status"
      data-testid="due-today-strip"
      className={`mb-4 rounded-lg border px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm ${
        overdue ? "bg-warning-soft border-warning-border text-warning-soft-foreground" : "bg-primary-soft border-primary-border text-foreground"
      }`}
    >
      <ListChecks className="w-4 h-4 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="font-medium">LinkedIn today:</span> {dueSummaryParts(counts).join(", ")}
        {overdue && <span className="font-medium"> · {counts.overdue} overdue</span>}
      </span>
      {counts.due_total > 0 || (counts.warm_leads ?? 0) > 0 ? <Link to={dueTodayPath()} className="shrink-0 font-medium underline underline-offset-2">Open due today</Link> : null}
      {replies > 0 && <Link to={conversationsPath(counts.conversations_waiting_on_us ? "waiting=us" : "quiet=1")} className="shrink-0 font-medium underline underline-offset-2">Open the Inbox</Link>}
    </div>
  );
}
